// src/components/Home.js
import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from "react";
import "./styles/Home.css";
import { useAuth } from "../providers/AuthProvider";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import ProjectDetails from "./ProjectDetails";
import Loader from "./Loader";
import { useLocation, useNavigate } from "react-router-dom";
import SurveyLoader from "./SurveyLoader";
import useDebounce from "../hooks/useDebounce";
import { FaSearch, FaTimes, FaSync, FaSpinner } from "react-icons/fa";
import ProjectButton from "./ProjectButton";

const SELECTED_PROJECT_KEY = "selectedProjectId";

// Only these users can pick the AI model for questionnaire conversion; everyone
// else is silently pinned to Gemini. Must match MODEL_SELECTION_EMAILS in
// eraserver/scriptingRoutes.py, which is the actual enforcement point.
const MODEL_SELECTION_EMAILS = new Set([
  "amit@excavateresearch.com",
  "anurag@excavateresearch.com",
  "dp@excavateresearch.com",
]);

function Home() {
  const navigate = useNavigate();
  const isFirstMount = useRef(true);
  const initialLoadDone = useRef(false); // guards the auto-select-on-load effect
  const [showForm, setShowForm] = useState(false);
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  // Refresh loading state
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [file, setFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [duplicateProjectWarning, setDuplicateProjectWarning] = useState(null); // { id, name }
  const [isUploading, setIsUploading] = useState(false);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);

  const [contents, setContents] = useState([]);
  const [error, setError] = useState(null);
  const [includeExtraData, setIncludeExtraData] = useState(false);
  const [titles, setTitles] = useState([]);
  const [shareEmails, setShareEmails] = useState("");
  const [shareMessage, setShareMessage] = useState("");

  // Progress tracking states
  const [processingMessages, setProcessingMessages] = useState([]);
  const [isProcessingSurvey, setIsProcessingSurvey] = useState(false);
  const [messageInterval, setMessageInterval] = useState(null);
  const [pollingTimeoutId, setPollingTimeoutId] = useState(null);
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);

  const [selectedModel, setSelectedModel] = useState("gemini");
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  // Search state for My Projects sidebar
  const [projectSearchQuery, setProjectSearchQuery] = useState("");

  // Cache states
  const [clientsCache, setClientsCache] = useState(null);
  const [clientsTimestamp, setClientsTimestamp] = useState(0);
  const [projectsCache, setProjectsCache] = useState({});

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  const {
    setOriginalDataRows,
    selectedProjectDetails,
    setSelectedProjectDetails,
    loading,
    setLoading,
    setBanners,
    setdataGroup,
    dataGroup,
    projectId,
    setProjectId,
    setWeightingData,
    setWeightingStats,
    setcurrentSelectedVariablesset,
    setSavedWeightings,
    setVariables,
    setSelectedClient,
    setSelectedProject,
    selectedProject,
    selectedClient,
    setSdataGroup,
    powner,
    setPowner,
    setSurveyFileName,
    setSurveyName,
    setPID,
    setPName,
    clearProjectContext
  } = useContext(VariableContext);
  const { selectedBanner, setselectedBanner } = useContext(FetchingFreq);
  const location = useLocation();

  // Get toast from context
  const { toast } = useToast();
  const { setUser, user, logout, loading: authLoading } = useAuth();
  const { instance, accounts } = useMsal();

  const isSavFile = useMemo(() => {
    return file?.name?.toLowerCase().endsWith(".sav");
  }, [file]);

  const canSelectModel = useMemo(() => {
    const email = user?.user?.email?.toLowerCase();
    return !!email && MODEL_SELECTION_EMAILS.has(email);
  }, [user]);

  const debouncedClientSearch = useDebounce(clientSearchTerm, 300);
  const debouncedProjectSearch = useDebounce(projectSearchTerm, 300);
  const debouncedProjectQuery = useDebounce(projectSearchQuery, 300);
  const [uploadMode, setUploadMode] = useState("truncate");

  // ⭐ DEFINE showToastMessage FIRST with safety check
  const showToastMessage = useCallback((message, type = 'info') => {
    if (toast && toast[type]) {
      toast[type](message);
    } else if (toast && toast.info) {
      toast.info(message);
    } else {
      // Fallback if toast is not available
      console.log(`[${type}] ${message}`);
      alert(message); // Remove this in production
    }
  }, [toast]);

  // Filter projects based on search query - UPDATED to include pid search
  const filteredProjectsBySearch = useMemo(() => {
    const allProjects = user?.user?.projects || [];
    if (!debouncedProjectQuery) return allProjects;

    const query = debouncedProjectQuery.toLowerCase();
    return allProjects.filter(project => {
      // Search by project name
      if (project.project_name?.toLowerCase().includes(query)) return true;

      // Search by client name
      if (project.client_name?.toLowerCase().includes(query)) return true;

      // Search by project ID
      if (project.id?.toString().includes(query)) return true;

      // Search by pid (project's pid field) - ONLY FOR SEARCH
      if (project.pid?.toString().includes(query)) return true;

      // Search by owner email
      if (project.owner?.toLowerCase().includes(query)) return true;

      // Search by owner name (part before @)
      const ownerName = project.owner?.split('@')[0]?.toLowerCase() || '';
      if (ownerName.includes(query)) return true;

      return false;
    });
  }, [user?.user?.projects, debouncedProjectQuery]);

  // Sorted and filtered projects
  const sortedProjects = useMemo(() => {
    return [...filteredProjectsBySearch].sort((a, b) => b.id - a.id);
  }, [filteredProjectsBySearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".home-autocomplete-wrapper")) {
        setShowClientDropdown(false);
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Landing on Home means the user is picking a project
  useEffect(() => {
    clearProjectContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    };
  }, [messageInterval]);

  const fetchContents = useCallback(async () => {
    try {
      const { data } = await apiService.post("/getListFolder", {
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
      });
      setContents(data.value);
    } catch (error) {
      setError(error.message);
    }
  }, [selectedProject, selectedClient]);

  const handleRefreshProjects = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      // accounts[0] (from useMsal()) can be briefly empty right after a
      // page refresh, before MSAL finishes re-hydrating its account state
      // into that hook - even though the account is already sitting in
      // MSAL's cache and the app's own session is still perfectly valid.
      // Falling back to instance.getAllAccounts()[0] (same pattern as
      // AuthProvider.js) reads that cache directly instead of relying on
      // the hook's timing, so a refresh doesn't force a false logout.
      const account = accounts[0] || instance.getAllAccounts()[0];
      if (!account) {
        console.error("No active account found");
        showToastMessage("Session expired. Please log in again.");
        logout();
        return;
      }

      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          account: account,
          scopes: ["openid", "profile", "email"],
        });
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          tokenResponse = await instance.acquireTokenPopup({
            account: account,
            scopes: ["openid", "profile", "email"],
          });
        } else {
          throw silentError;
        }
      }

      if (!tokenResponse?.idToken) {
        throw new Error("No ID token received");
      }

      const response = await apiService.loginWithMicrosoft(tokenResponse.idToken);

      if (response.status === 200 && response.data) {
        setUser(response.data);
        localStorage.setItem("user", JSON.stringify(response.data));
        showToastMessage("Projects refreshed successfully!", "success");
      } else {
        throw new Error("Failed to refresh user data");
      }
    } catch (error) {
      console.error("Failed to refresh user projects:", error);

      if (error.message?.includes("401") ||
        error.message?.includes("Session") ||
        error.message?.includes("expired")) {
        showToastMessage("Session expired. Please login again.");
        logout();
      } else {
        showToastMessage("Failed to refresh projects list");
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [instance, accounts, isRefreshing, setUser, logout, showToastMessage]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      showToastMessage("Please select a file before uploading.");
      return;
    }

    let clientData = selectedClient;
    let projectData = selectedProject;

    if (!clientData || !clientData.name) {
      showToastMessage("Please select or enter a client name.");
      return;
    }

    if (!projectData || !projectData.name) {
      showToastMessage("Please select or enter a project name.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user", JSON.stringify(user));
    formData.append("project", JSON.stringify(projectData));
    formData.append("client", JSON.stringify(clientData));
    formData.append("includeExtraData", includeExtraData ? true : false);
    formData.append("uploadMode", uploadMode);

    setLoading(true);
    setIsUploading(true);

    try {
      const { data } = await apiService.upload("/upload", formData);
      let createdProjectId = data.project_id;
      const finalDataGroup = data.qgroup;

      const sortedKeys = Object.keys(finalDataGroup).sort(
        (a, b) => finalDataGroup[a].order - finalDataGroup[b].order
      );
      const sortedGroup = sortedKeys.reduce((acc, key) => {
        acc[key] = finalDataGroup[key];
        return acc;
      }, {});

      const finalDetails = {
        qgroup_json: sortedGroup,
        id: createdProjectId,
        client: data.client || clientData,
        project: data.project || projectData,
        filename: data.filename,
        uploaded_time: data.uploaded_time,
        has_database: data.has_database,
      };

      showToastMessage(data.message || "File uploaded successfully.");

      const extractedTitles = Object.keys(sortedGroup);
      setProjectId(createdProjectId);
      setTitles(extractedTitles);
      setVariables(extractedTitles);
      setSelectedProjectDetails(finalDetails);
      setPowner(user.user.email);
      setdataGroup(sortedGroup);
      setShowForm(false);
      setFile(null);

      fetchContents();
      await handleRefreshProjects();

    } catch (error) {
      console.error("Upload error:", error);

      if (error.message?.includes("401") || error.message?.includes("Session")) {
        showToastMessage("Session expired. Please login again.");
        logout();
      } else {
        showToastMessage(error.message || "An error occurred while uploading the file.");
      }
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  }, [
    file, selectedClient, selectedProject, user, includeExtraData,
    setLoading, setProjectId, setVariables, setdataGroup, fetchContents,
    handleRefreshProjects, setSelectedProjectDetails, setPowner, logout, showToastMessage
  ]);

  const handleSurvey = useCallback(async () => {
    if (!file) {
      showToastMessage("Please select a file before uploading.");
      return;
    }

    let clientData = selectedClient;
    let projectData = selectedProject;

    if (!clientData || !clientData.name) {
      showToastMessage("Please select or enter a client name.");
      return;
    }

    if (!projectData || !projectData.name) {
      showToastMessage("Please select or enter a project name.");
      return;
    }

    if (duplicateProjectWarning) {
      const proceed = window.confirm(
        `A project named "${duplicateProjectWarning.name}" (ID: ${duplicateProjectWarning.id}) already exists. Are you sure you want to upload it again?`
      );
      if (!proceed) return;
    }

    const formData = new FormData();
    formData.append("questionnaire", file);
    if (excelFile) {
      formData.append("excel", excelFile);
    }
    formData.append("user", JSON.stringify(user));
    formData.append("project", JSON.stringify(projectData));
    formData.append("client", JSON.stringify(clientData));
    formData.append("includeExtraData", includeExtraData);

    const modelToUse = excelFile ? "gemini" : selectedModel;
    formData.append("model", modelToUse);

    setIsProcessingSurvey(true);
    setSurveyFileName(file.name);
    setSurveyName(file.name.replace(/\.[^/.]+$/, ""));

    const startTime = Date.now();
    const elapsedTimer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const mins = Math.floor(elapsed / 60);
      const secs = elapsed % 60;
      const label = mins > 0
        ? `Processing… ${mins}m ${secs}s elapsed`
        : `Processing… ${secs}s elapsed`;
      setProcessingMessages([label]);
    }, 1000);

    try {
      const { data } = await apiService.upload("/surveyuploadnew", formData, {
        timeout: 5400000,
      });

      showToastMessage(
        `✅ Survey processed successfully using ${(data.model_used || "gemini").toUpperCase()} model`
      );
      setPID(data.pid);
      setPName(data.pname);
      setSdataGroup(data.sqgroup);
      setProjectId(data.project_id);
      setSelectedProjectDetails({
        sqgroup_json: data.sqgroup,
        id: data.project_id,
        client: data.client || clientData,
        project: data.project || projectData,
        filename: data.filename,
        uploaded_time: data.uploaded_time,
        has_database: data.has_database,
      });

      setIsProcessingSurvey(false);
      navigate("/surveybuilder");
      handleRefreshProjects().catch(() => { });
    } catch (error) {
      if (error.message?.includes("401") || error.message?.includes("Session")) {
        showToastMessage("Session expired. Please login again.");
        logout();
      } else {
        showToastMessage(
          error.message?.includes("timed out")
            ? "Processing timed out — the questionnaire may be very large. Please try again."
            : "An error occurred while uploading the file."
        );
      }
      setIsProcessingSurvey(false);
    } finally {
      clearInterval(elapsedTimer);
      setProcessingMessages([]);
    }
  }, [
    file, excelFile, selectedClient, selectedProject, user, includeExtraData,
    selectedModel, setSdataGroup, setProjectId, handleRefreshProjects, navigate,
    setProcessingMessages, logout, showToastMessage, duplicateProjectWarning
  ]);

  const getProjectList = useCallback(async (client) => {
    let clientId;
    if (typeof client === "object" && client !== null) {
      clientId = client.id;
    } else {
      clientId = client;
    }

    if (!clientId) {
      console.error("No client ID provided");
      return;
    }

    const now = Date.now();
    if (projectsCache[clientId] && now - projectsCache[clientId].timestamp < CACHE_DURATION) {
      setProjects(projectsCache[clientId].data);
      return;
    }

    try {
      const { data } = await apiService.post("/projects", { clientId });
      setProjects(data);
      setProjectsCache((prev) => ({
        ...prev,
        [clientId]: { data: data, timestamp: now },
      }));
    } catch (error) {
      console.error("Error fetching project list:", error);

      if (error.message?.includes("401") || error.message?.includes("Session")) {
        showToastMessage("Session expired. Please login again.");
        logout();
      } else {
        showToastMessage("An error occurred while fetching ProjectList.");
      }
    }
  }, [projectsCache, showToastMessage, logout]);

  const fetchClientsProjects = useCallback(async () => {
    const now = Date.now();
    if (clientsCache && now - clientsTimestamp < CACHE_DURATION) {
      setClients(clientsCache);
      return;
    }

    try {
      setLoading(true);
      const { data } = await apiService.get("/clients");
      setClients(data);
      setClientsCache(data);
      setClientsTimestamp(now);
    } catch (error) {
      console.error("Failed to fetch clients and projects:", error);

      if (error.message?.includes("401") || error.message?.includes("Session")) {
        showToastMessage("Session expired. Please login again.");
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [clientsCache, clientsTimestamp, setLoading, showToastMessage, logout]);

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setDuplicateProjectWarning(null);

    if (!selectedFile?.name?.toLowerCase().endsWith(".sav")) {
      setIncludeExtraData(false);

      // Check if a project with the same questionnaire name already exists
      const allProjects = user?.user?.projects || [];
      const baseName = selectedFile?.name?.replace(/\.[^/.]+$/, "").toLowerCase() || "";
      if (baseName) {
        const match = allProjects.find(
          (p) => (p.project_name || "").toLowerCase() === baseName
        );
        if (match) {
          setDuplicateProjectWarning({ id: match.id, name: match.project_name });
        }
      }
    }
  }, [user]);

  const handleExcelChange = useCallback((e) => {
    setExcelFile(e.target.files[0]);
  }, []);

  // Select a project → persist the choice to localStorage so a refresh or
  // a fresh mount (same session) restores the same project instead of
  // silently jumping back to the first one in the list.
  const handleProjectClick = useCallback((project) => {
    clearProjectContext();
    setSelectedProjectId(project.id);

    try {
      localStorage.setItem(SELECTED_PROJECT_KEY, project.id.toString());
    } catch (error) {
      console.error("Failed to persist selected project:", error);
    }

    setLoading(true);
    apiService.get(`/project/${project.id}/files`)
      .then(({ data }) => {
        if (data.length > 0 && (data[0].qgroup_json || data[0].sqgroup_json)) {
          const qgroup = data[0].qgroup_json;
          if (qgroup) {
            const sortedKeys = Object.keys(qgroup)
              .filter((key) => qgroup[key] != null)
              .sort((a, b) => (qgroup[a].order ?? 0) - (qgroup[b].order ?? 0));
            const sortedGroup = sortedKeys.reduce((acc, key) => {
              acc[key] = qgroup[key];
              return acc;
            }, {});
            setdataGroup(sortedGroup || []);
          }
          setProjectId(project.id);
          setSelectedClient(data[0].client);
          setSelectedProject(data[0].project);
          setSdataGroup(data[0].sqgroup_json || []);
          setPID(data[0].pid || null);
          setPName(data[0].pname || null);
          setBanners(data[0].banner_data);
          setPowner(project.owner);
          setselectedBanner([]);
          setWeightingData({});
          setSavedWeightings(data[0].weighting);
          if (data[0].weighting) {
            setWeightingStats({ ...data[0].weighting["weight"].stats });
            setcurrentSelectedVariablesset(
              Object.keys(data[0].weighting["weight"]["weightedCounts"] || {})
            );
          }

          setShowForm(false);
          setSelectedProjectDetails(data[0]);

          const tables = data[0]?.tables || {};
          const baseRows = tables.table_single?.length > 0 ? tables.table_single : [];
          const numericRows = tables.table_numeric || [];
          const openEndedRows = tables.table_openended || [];

          const numericMap = Object.fromEntries(numericRows.map((r) => [r.id, r]));
          const openMap = Object.fromEntries(openEndedRows.map((r) => [r.id, r]));

          const mergedRows = baseRows.map((row) => ({
            ...row,
            ...(numericMap[row.id] || {}),
            ...(openMap[row.id] || {}),
          }));

          setOriginalDataRows(mergedRows);
        }
      })
      .catch((error) => {
        console.error("There has been a problem with your fetch operation:", error);

        if (error.message?.includes("401") || error.message?.includes("Session")) {
          showToastMessage("Session expired. Please login again.");
          logout();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, setdataGroup, setProjectId, setSelectedClient, setSelectedProject,
    setSdataGroup, setBanners, setselectedBanner, setWeightingData, setSavedWeightings,
    setWeightingStats, setcurrentSelectedVariablesset, setSelectedProjectDetails,
    setOriginalDataRows, setPowner, setPID, setPName, clearProjectContext,
    showToastMessage, logout]);

  // On first load with a project list available: restore whichever project
  // was last selected (from localStorage) if it still belongs to this
  // user's project list; otherwise fall back to the first project. A
  // stale/foreign id (e.g. leftover from a previous user on a shared
  // machine, or a since-deleted project) is cleared rather than trusted.
  useEffect(() => {
    if (sortedProjects.length === 0 || selectedProjectId || initialLoadDone.current) {
      return;
    }

    let storedId = null;
    try {
      storedId = localStorage.getItem(SELECTED_PROJECT_KEY);
    } catch (error) {
      console.error("Failed to read selected project from storage:", error);
    }

    const storedProject = storedId
      ? sortedProjects.find((p) => p.id.toString() === storedId)
      : null;

    if (storedId && !storedProject) {
      // Stored id doesn't belong to this user's current project list —
      // don't silently reuse it.
      try {
        localStorage.removeItem(SELECTED_PROJECT_KEY);
      } catch (error) {
        console.error("Failed to clear stale selected project:", error);
      }
    }

    handleProjectClick(storedProject || sortedProjects[0]);
    initialLoadDone.current = true;
  }, [sortedProjects, selectedProjectId, handleProjectClick]);

  const handleCreateProjectClick = useCallback(async () => {
    if (!showForm) {
      await fetchClientsProjects();
    }
    setShowForm(!showForm);

    if (showForm) {
      setSelectedClient(null);
      setSelectedProject(null);
      setClientSearchTerm("");
      setProjectSearchTerm("");
      setFile(null);
      setExcelFile(null);
      setDuplicateProjectWarning(null);
      setSelectedModel("gemini");
    }
  }, [showForm, fetchClientsProjects]);

  const filteredClients = useMemo(() => {
    return clients.filter((client) =>
      client.name.toLowerCase().includes(debouncedClientSearch.toLowerCase())
    );
  }, [clients, debouncedClientSearch]);

  const filteredProjectsList = useMemo(() => {
    return projects.filter((project) =>
      project.name.toLowerCase().includes(debouncedProjectSearch.toLowerCase())
    );
  }, [projects, debouncedProjectSearch]);

  const clearSearch = useCallback(() => {
    setProjectSearchQuery("");
  }, []);

  if (!user || !user.user || !user.user.projects) {
    return <div className="home-loading">Loading...</div>;
  }

  return (
    <div className="home-container">
      <div className="home-sidebar">
        <div className="home-sidebar-header">
          <h2>My Projects</h2>
          <button
            className={`home-refresh-btn-icon ${isRefreshing ? 'refreshing' : ''}`}
            onClick={handleRefreshProjects}
            title="Refresh Projects"
            disabled={isRefreshing}
          >
            {isRefreshing ? <FaSpinner className="spinning" /> : <FaSync />}
          </button>
        </div>

        <div className="home-project-search-wrapper">
          <FaSearch className="home-search-icon" />
          <input
            type="text"
            placeholder="Search by name, client, owner, ID or PID..."
            value={projectSearchQuery}
            onChange={(e) => setProjectSearchQuery(e.target.value)}
            className="home-project-search-input"
          />
          {projectSearchQuery && (
            <button className="home-clear-search-btn" onClick={clearSearch}>
              <FaTimes />
            </button>
          )}
        </div>

        {debouncedProjectQuery && (
          <div className="home-search-result-count">
            Found {sortedProjects.length} project{sortedProjects.length !== 1 ? 's' : ''}
          </div>
        )}

        <div className="home-project-list">
          {sortedProjects.length > 0 ? (
            sortedProjects.map((project) => (
              <ProjectButton
                key={project.id}
                project={project}
                isSelected={selectedProjectId === project.id}
                onClick={() => handleProjectClick(project)}
              />
            ))
          ) : (
            <div className="home-no-projects-found">
              {debouncedProjectQuery ? "No projects match your search" : "No projects found"}
            </div>
          )}
        </div>
      </div>

      <div className="home-content">
        {loading ? (
          <Loader loading={true} />
        ) : isProcessingSurvey ? (
          <SurveyLoader loading={true} messages={processingMessages} />
        ) : (
          <>
            <h3>Welcome to the Dashboard</h3>
            <div className="home-content-header">
              <p>Select a project or create a new one to get started.</p>
              <button
                className="home-create-project-btn"
                onClick={handleCreateProjectClick}
              >
                {showForm ? "Close" : "Create New Project"}
              </button>
            </div>
            {showForm && (
              <form className="home-project-form" onSubmit={(e) => e.preventDefault()}>
                <div className="home-form-group">
                  <label htmlFor="clientName">Client Name</label>
                  <div className="home-autocomplete-wrapper">
                    <input
                      type="text"
                      placeholder="Type or select a client"
                      value={clientSearchTerm}
                      onChange={(e) => {
                        setClientSearchTerm(e.target.value);
                        setShowClientDropdown(true);
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      className="home-autocomplete-input"
                    />
                    {showClientDropdown && (
                      <div className="home-autocomplete-dropdown">
                        {filteredClients.map((client) => (
                          <div
                            key={client.id}
                            className="home-autocomplete-item"
                            onClick={() => {
                              setSelectedClient(client);
                              setClientSearchTerm(client.name);
                              setShowClientDropdown(false);
                              getProjectList(client.id);
                            }}
                          >
                            {client.name}
                          </div>
                        ))}
                        {clientSearchTerm &&
                          !clients.some(
                            (c) =>
                              c.name.toLowerCase() === clientSearchTerm.toLowerCase()
                          ) && (
                            <div
                              className="home-autocomplete-item create-new"
                              onClick={() => {
                                setSelectedClient({ id: null, name: clientSearchTerm });
                                setShowClientDropdown(false);
                              }}
                            >
                              + Create new: "{clientSearchTerm}"
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="home-form-group">
                  <label htmlFor="projectName">Project Name</label>
                  <div className="home-autocomplete-wrapper">
                    <input
                      type="text"
                      placeholder="Type or select a project"
                      value={projectSearchTerm}
                      onChange={(e) => {
                        setProjectSearchTerm(e.target.value);
                        setShowProjectDropdown(true);
                      }}
                      onFocus={() => setShowProjectDropdown(true)}
                      disabled={!selectedClient?.name}
                      className="home-autocomplete-input"
                    />
                    {showProjectDropdown && selectedClient?.name && (
                      <div className="home-autocomplete-dropdown">
                        {filteredProjectsList.map((project) => (
                          <div
                            key={project.id}
                            className="home-autocomplete-item"
                            onClick={() => {
                              setSelectedProject(project);
                              setProjectSearchTerm(project.name);
                              setShowProjectDropdown(false);
                            }}
                          >
                            {project.name}
                          </div>
                        ))}
                        {projectSearchTerm &&
                          !projects.some(
                            (p) =>
                              p.name.toLowerCase() === projectSearchTerm.toLowerCase()
                          ) && (
                            <div
                              className="home-autocomplete-item create-new"
                              onClick={() => {
                                setSelectedProject({ id: null, name: projectSearchTerm });
                                setShowProjectDropdown(false);
                              }}
                            >
                              + Create new: "{projectSearchTerm}"
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="home-form-group">
                  <label htmlFor="file">Upload File</label>
                  <input
                    type="file"
                    id="file"
                    accept=".sav,.pdf,.docx,.doc,.xls,.xlsx"
                    onChange={handleFileChange}
                    required
                  />
                  {duplicateProjectWarning && (
                    <p style={{ fontSize: "0.82em", color: "#92400e", marginTop: "6px", padding: "8px 10px", background: "#fffbeb", borderRadius: "6px", border: "1px solid #fcd34d" }}>
                      ⚠️ A project named <strong>"{duplicateProjectWarning.name}"</strong> (ID: {duplicateProjectWarning.id}) already exists. You will be asked to confirm before uploading.
                    </p>
                  )}
                </div>

                {isSavFile && (
                  <>
                    <div className="home-form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={includeExtraData}
                          onChange={(e) => setIncludeExtraData(e.target.checked)}
                        />
                        Save Data
                      </label>
                    </div>

                    <div className="home-form-group">
                      <p style={{ fontSize: "0.8em", color: "#5b7fa6", marginTop: "4px", padding: "8px 10px", background: "#eef4ff", borderRadius: "6px", border: "1px solid #c3d9f5" }}>
                        To upload a questionnaire for this SPSS project, use the <strong>Tab Specs</strong> feature in <strong>Data Tables</strong> after the project is created.
                      </p>
                    </div>
                  </>
                )}

                {!isSavFile && (
                  <div className="home-form-group">
                    <label htmlFor="excel">Upload Excel Data (Optional)</label>
                    <input
                      type="file"
                      id="excel"
                      accept=".xlsx,.xls"
                      onChange={handleExcelChange}
                    />
                    {excelFile && <span className="home-file-info">✓ {excelFile.name}</span>}
                  </div>
                )}

                {!isSavFile && canSelectModel && (
                  <div className="home-form-group">
                    <label>AI Model</label>
                    <div className="home-model-options">
                      <label className="home-model-option">
                        <input
                          type="radio"
                          name="model"
                          value="gemini"
                          checked={selectedModel === "gemini"}
                          onChange={(e) => setSelectedModel(e.target.value)}
                        />
                        <span>Gemini</span>
                      </label>
                      <label className="home-model-option">
                        <input
                          type="radio"
                          name="model"
                          value="claude"
                          checked={selectedModel === "claude"}
                          onChange={(e) => setSelectedModel(e.target.value)}
                        />
                        <span>Claude</span>
                      </label>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="home-submit-btn"
                  onClick={() => {
                    if (!file) {
                      showToastMessage("Please select a file before uploading.");
                      return;
                    }
                    if (isUploading) return;

                    if (file.name.toLowerCase().endsWith(".sav")) {
                      handleUpload();
                    } else {
                      handleSurvey();
                    }
                  }}
                  disabled={isUploading || loading}
                >
                  {isUploading ? "Processing..." : "Submit"}
                </button>
              </form>
            )}
            {!showForm && (
              <ProjectDetails
                project={selectedProjectDetails}
                projectid={projectId}
                projectOwner={powner}
                onProjectsRefresh={handleRefreshProjects}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Home;